import { ui } from "@patient-watch/ui";
import { z } from "@/utils/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { type SubmitHandler, useForm } from "react-hook-form";
import { commonComponents as cc } from "@/components/common";
import { Email, Textarea } from "@patient-watch/forms";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useDeleteRequest } from "@/features/auth/api/useDeleteRequest";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { hooks as h } from "@/hooks";

export const SchemaZ = z.object({
    email: z.string().email().min(1, "Email is required"),
    reason: z.string().min(1, "Reason is required"),
    confirmation: z
        .string()
        .min(1, "Confirmation is required")
        .refine((value) => value.toLowerCase() === "delete my account", {
            message: 'You must type "delete my account" to confirm',
        }),
});

export type SchemaT = z.infer<typeof SchemaZ>;

const DeleteAccountPage = () => {
    const copy = useProfileCopy();
    const deleteRequest = useDeleteRequest();

    h.useNav([
        { label: copy.title, route: { to: "/profile" } },
        { label: copy.deleteAccount, route: { to: "/profile/delete-account" } },
    ]);

    const form = useForm<SchemaT>({
        resolver: zodResolver(SchemaZ),
        defaultValues: {
            email: "",
            reason: "",
            confirmation: "",
        },
    });

    const onSubmit: SubmitHandler<SchemaT> = async (formData) => {
        await deleteRequest.mutate({
            email: formData.email,
            reason: formData.reason,
        });
    };

    return (
        <>
            <cc.Title words={copy.deleteAccount} description={copy.deleteAccountPageDescription} />
            <ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                    <Email label={copy.email} />
                    <Textarea name="reason" label={copy.deleteAccountReason} />
                    <ui.FormField
                        control={form.control}
                        name="confirmation"
                        render={({ field }) => (
                            <ui.FormItem>
                                <ui.FormLabel>{copy.deleteAccountConfirmationLabel}</ui.FormLabel>
                                <ui.FormDescription>{copy.deleteAccountConfirmDescription}</ui.FormDescription>
                                <ui.FormControl>
                                    <ui.Input placeholder={copy.deleteAccountPlaceholder} {...field} />
                                </ui.FormControl>
                                <ui.FormMessage />
                            </ui.FormItem>
                        )}
                    />
                    <fc.DeleteButton
                        tabIndex={0}
                        className="w-full"
                        buttonType="submit"
                        isLoading={deleteRequest.isPending}
                        label={copy.deleteAccountSubmit}
                    />
                </form>
            </ui.Form>
        </>
    );
};

export const Route = createFileRoute("/_app/profile/delete-account")({
    component: DeleteAccountPage,
});
